import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Sun, Moon, Settings } from 'lucide-react';
import type { TranslationKeys } from '@/core/i18n/translations';

type Lang = 'en' | 'pl';

export interface WorkoutHomeHeaderProps {
  t: TranslationKeys;
  /** When true, the upgrade CTA is hidden (user already has PRO). */
  isPro: boolean;
  session: unknown;
  user: { email?: string | null } | null | undefined;
  theme: 'light' | 'dark';
  language: Lang;
  setLanguage: (l: Lang) => void;
  setTheme: (t: 'light' | 'dark') => void;
  setShowPricingModal: (v: boolean) => void;
  setShowAuthModal: (v: boolean) => void;
  handleLogout: () => void;
}

export function WorkoutHomeHeader({
  t,
  isPro,
  session,
  user,
  theme,
  language,
  setLanguage,
  setTheme,
  setShowPricingModal,
  setShowAuthModal,
  handleLogout,
}: WorkoutHomeHeaderProps) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-4 border-b border-blue-200 dark:border-slate-800 pb-6">
      <div className="flex items-center space-x-3 min-w-0 flex-1 sm:flex-initial">
        <div className="p-3 bg-orange-600 rounded-lg shadow-lg shrink-0">
          <Activity className="w-8 h-8 text-white" />
        </div>
        <div className="leading-tight min-w-0">
          {/*
            Visually styled like a heading, but downgraded to <h2> so the document keeps
            exactly one canonical <h1> (the hidden brand H1 declared in index.html).
          */}
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Prompt2Train
          </h2>
          <p className="text-[11px] leading-snug sm:text-sm text-gray-500 dark:text-slate-400 mt-0.5 max-w-[18rem] sm:max-w-md">
            {t.appTagline}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:gap-3 justify-end">
        {session ? (
          <Link
            to="/settings"
            data-testid="header-settings-link"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label={t.settingsTitle}
          >
            <Settings className="w-5 h-5" aria-hidden />
          </Link>
        ) : null}
        {session ? (
          <span className="hidden sm:inline text-sm text-slate-600 dark:text-slate-300 max-w-[14rem] truncate">
            {user?.email}
          </span>
        ) : null}
        {session ? (
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-gray-400 hover:text-gray-600 hover:underline dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
          >
            {t.logout}
          </button>
        ) : (
          <button
            type="button"
            data-testid="header-login-btn"
            onClick={() => setShowAuthModal(true)}
            className="text-sm font-semibold rounded-lg border-2 border-orange-500 bg-orange-50 px-3 py-1.5 text-orange-700 shadow-sm hover:bg-orange-600 hover:text-white hover:border-orange-600 dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-400 dark:hover:bg-orange-600 dark:hover:text-white transition-colors"
          >
            {t.loginSignUp}
          </button>
        )}
        <button
          type="button"
          data-testid="language-toggle-btn"
          onClick={() => setLanguage(language === 'en' ? 'pl' : 'en')}
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 border rounded-lg bg-white dark:bg-slate-900 touch-manipulation"
          aria-label={language === 'en' ? 'Switch language to Polish' : 'Switch language to English'}
        >
          {language === 'en' ? 'EN' : 'PL'}
        </button>
        <button
          type="button"
          data-testid="theme-toggle-btn"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 border rounded-lg bg-white dark:bg-slate-900 touch-manipulation"
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        {!isPro ? (
          <button
            type="button"
            onClick={() => setShowPricingModal(true)}
            className="min-h-[44px] px-4 py-2 border rounded-lg bg-orange-600 text-white hover:bg-orange-500 touch-manipulation"
          >
            {t.upgradeToProBtn}
          </button>
        ) : null}
      </div>
    </header>
  );
}
